import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';

import classNames from 'classnames';
import { getAmazonPayButtonSettings } from '../../services/amazonPay/amazonPay-migrated';
import styles from './AmazonPayButton.module.scss';
import messages from '../forms/SteppedPaymentForm/messages';
import { IS_NORMAL_AMAZON_CHECKOUT_KEY } from '../CompleteOrderButton';

import { clearPaymentMethodsData } from '../../slices/payments';
import { CartInStore } from '../../slices/cart/types';
import { CustomDocumentForAmazonPay, AmazonCheckoutConfig } from '../../services/amazonPay/types';

const AmazonPayButton = ({
  id,
  onClick = undefined,
  isSteppedCheckout = false
}: {
  id: string;
  onClick?: () => void;
    isSteppedCheckout?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const { currencyCode, cartId, isDraft } = useSelector<{
      cart: CartInStore;
    }>((state) => ({
    currencyCode: state.cart?.totalPrice?.currencyCode,
    cartId: state.cart?.id,
    isDraft: state.cart?.isDraft,
  })) as {
      cartId: string;
      isDraft: boolean;
      currencyCode: string;
    };
  const dispatch = useDispatch();

  // render two hidden Amazon Pay Button
  // one for pay only and another for pay and ship
  const renderAmazonPayButton = useCallback(async () => {
    if (
      document.getElementById('_amazonPayButton') &&
      (document as CustomDocumentForAmazonPay).amazonPayButtonInitCheckout
    ) {
      return;
    }

    const divContainer = document.createElement('div');
    divContainer.id = '_amazonPayButton';
    divContainer.style.visibility = 'hidden';
    divContainer.style.display = 'none';
    document.getElementsByTagName('body')[0].appendChild(divContainer);
    const divContainer2 = document.createElement('div');
    divContainer2.id = '_amazonPayButtonPayOnly';
    divContainer2.style.visibility = 'hidden';
    divContainer2.style.display = 'none';
    document.getElementsByTagName('body')[0].appendChild(divContainer2);

    // get button setting from ms
    const { createCheckoutSessionConfig, ...amazonButtonSettings }: AmazonCheckoutConfig =
      await getAmazonPayButtonSettings(cartId, isDraft);

    // call amazon library to render buttons
    const amzBtnRef: AmazonPayButton = window.amazon.Pay.renderButton(
      '#_amazonPayButton',
      {
        ...amazonButtonSettings,
        ledgerCurrency: currencyCode,
        placement: 'Cart',
        productType: 'PayAndShip',
      },
    );

    const amzBtnRefPayOnly: AmazonPayButton = window.amazon.Pay.renderButton(
      '#_amazonPayButtonPayOnly',
      {
        ...amazonButtonSettings,
        ledgerCurrency: currencyCode,
        placement: 'Cart',
        productType: 'PayOnly',
      },
    );

    // save references to init checkout functions
    (document as CustomDocumentForAmazonPay)
      .amazonPayButtonInitCheckout = amzBtnRef.initCheckout;
    (document as CustomDocumentForAmazonPay)
      .amazonPayButtonInitCheckoutPayOnly = amzBtnRefPayOnly.initCheckout;
  }, [cartId, currencyCode, isDraft]);

  useEffect(() => {
    // create Amazon Pay script
    const createAmazonPayScript = () => {
      if (process.env.AMAZON_PAY_CHECKOUT_URL) {
        const s = document.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = process.env.AMAZON_PAY_CHECKOUT_URL;
        s.onload = renderAmazonPayButton;
        document.getElementsByTagName('body')[0].appendChild(s);
      }
    };

    if (!window.amazon) {
      createAmazonPayScript();
    }
  }, [renderAmazonPayButton]);

  const onClickHandler = () => {
    (async () => {
      if (onClick) {
        onClick();
      }
      dispatch(clearPaymentMethodsData());
      
      try {
        const amazonButtonSettings: AmazonCheckoutConfig =
          await getAmazonPayButtonSettings(cartId, isDraft);
        
        if (!(document as CustomDocumentForAmazonPay).amazonPayButtonInitCheckout) {
          await renderAmazonPayButton();
        }

        const isNormalAmazonCheckoutKey = localStorage.getItem(IS_NORMAL_AMAZON_CHECKOUT_KEY);
        const initCheckout = isNormalAmazonCheckoutKey
          ? (document as CustomDocumentForAmazonPay).amazonPayButtonInitCheckoutPayOnly
          : (document as CustomDocumentForAmazonPay).amazonPayButtonInitCheckout;

        await initCheckout({
          createCheckoutSessionConfig: {
            ...amazonButtonSettings.createCheckoutSessionConfig,
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error initializing Amazon Pay:', error);
      }
    })();
  };


  return (
    <div className={classNames(styles['amazon-pay-button-wrapper'], {
      [styles['bg-black']]: !isSteppedCheckout,
      [styles['bg-yellow']]: isSteppedCheckout && id === 'MainBlockAmzBtn',
      [styles['bg-yellow-full-width']]: id === 'PaymentBlockAmzBtn'
    })}>
      <button
        type="button"
        id={`${id}_button`}
        onClick={onClickHandler}
        aria-label={formatMessage(messages.amazonPayLogoAlt)}
      >
        <div
          className={classNames({
            [styles.amazonlogo]: !isSteppedCheckout,
            [styles['amazonlogo-yellow']]: isSteppedCheckout && id === 'MainBlockAmzBtn',
            [styles['amazonlogo-big-yellow']]: id === 'PaymentBlockAmzBtn'
          })}
        />
      </button>
    </div>
  );
};

export default AmazonPayButton;